import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type AIProvider = 'openai' | 'anthropic' | 'deepseek';

export interface AIModel {
  id: string;
  name: string;
  provider: AIProvider;
  description: string;
  contextWindow: string;
  pricing: string;
}

export const AI_MODELS: Record<AIProvider, AIModel[]> = {
  openai: [
    {
      id: 'gpt-5-2025-08-07',
      name: 'GPT-5',
      provider: 'openai',
      description: 'Flagship model with exceptional performance',
      contextWindow: '200K',
      pricing: 'Premium'
    },
    {
      id: 'gpt-5-mini-2025-08-07',
      name: 'GPT-5 Mini',
      provider: 'openai',
      description: 'Faster, cost-efficient version of GPT-5',
      contextWindow: '200K',
      pricing: 'Standard'
    },
    {
      id: 'gpt-5-nano-2025-08-07',
      name: 'GPT-5 Nano',
      provider: 'openai',
      description: 'Fastest, cheapest version for summarization',
      contextWindow: '128K',
      pricing: 'Economy'
    },
    {
      id: 'gpt-4.1-2025-04-14',
      name: 'GPT-4.1',
      provider: 'openai',
      description: 'Reliable flagship GPT-4 model',
      contextWindow: '128K',
      pricing: 'Standard'
    },
    {
      id: 'o3-2025-04-16',
      name: 'O3',
      provider: 'openai',
      description: 'Powerful reasoning model for complex analysis',
      contextWindow: '200K',
      pricing: 'Premium'
    },
    {
      id: 'o4-mini-2025-04-16',
      name: 'O4 Mini',
      provider: 'openai',
      description: 'Fast reasoning model optimized for coding',
      contextWindow: '128K',
      pricing: 'Standard'
    }
  ],
  anthropic: [
    {
      id: 'claude-opus-4-20250514',
      name: 'Claude 4 Opus',
      provider: 'anthropic',
      description: 'Most capable with superior reasoning',
      contextWindow: '200K',
      pricing: 'Premium'
    },
    {
      id: 'claude-sonnet-4-20250514',
      name: 'Claude 4 Sonnet',
      provider: 'anthropic',
      description: 'High-performance with exceptional efficiency',
      contextWindow: '200K',
      pricing: 'Standard'
    },
    {
      id: 'claude-3-5-haiku-20241022',
      name: 'Claude 3.5 Haiku',
      provider: 'anthropic',
      description: 'Fastest model for quick responses',
      contextWindow: '200K',
      pricing: 'Economy'
    },
    {
      id: 'claude-3-5-sonnet-20241022',
      name: 'Claude 3.5 Sonnet',
      provider: 'anthropic',
      description: 'Previous intelligent model',
      contextWindow: '200K',
      pricing: 'Standard'
    }
  ],
  deepseek: [
    {
      id: 'deepseek-v3',
      name: 'DeepSeek V3',
      provider: 'deepseek',
      description: 'Advanced reasoning and coding capabilities',
      contextWindow: '128K',
      pricing: 'Economy'
    },
    {
      id: 'deepseek-coder-v2',
      name: 'DeepSeek Coder V2',
      provider: 'deepseek',
      description: 'Specialized for coding and development',
      contextWindow: '128K',
      pricing: 'Economy'
    },
    {
      id: 'deepseek-chat',
      name: 'DeepSeek Chat',
      provider: 'deepseek',
      description: 'General purpose conversational model',
      contextWindow: '64K',
      pricing: 'Budget'
    }
  ]
};

interface AIModelStore {
  selectedModel: AIModel;
  selectedProvider: AIProvider;
  setSelectedModel: (model: AIModel) => void;
  setSelectedProvider: (provider: AIProvider) => void;
  getModelsByProvider: (provider: AIProvider) => AIModel[];
  getAllModels: () => AIModel[];
}

/**
 * ORGANIZATION SCOPING: intentionally NOT org-scoped.
 *
 * The selected provider/model is a preference about how the user likes to work,
 * not a fact about an organization's data — the catalogue is a hard-coded
 * constant in this file. It is deliberately absent from the `@/lib/org-scope`
 * registry, and persists under one global key, so switching orgs does not reset
 * the user's model choice.
 */
export const useAIModelStore = create<AIModelStore>()(
  persist(
    (set) => ({
      selectedProvider: 'openai',
      selectedModel: AI_MODELS.openai[0],
      
      setSelectedModel: (model: AIModel) => set({ selectedModel: model }),
      
      setSelectedProvider: (provider: AIProvider) => {
        // Auto-select first model of the new provider
        set({
          selectedProvider: provider,
          selectedModel: AI_MODELS[provider][0]
        });
      },
      
      getModelsByProvider: (provider: AIProvider) => {
        return AI_MODELS[provider] || [];
      },
      
      getAllModels: () => {
        return Object.values(AI_MODELS).flat();
      }
    }),
    {
      name: 'ai-model-storage',
      partialize: (state) => ({
        selectedProvider: state.selectedProvider,
        selectedModel: state.selectedModel
      })
    }
  )
);

// Hook for backward compatibility with the old context API
export const useAIModel = () => {
  const store = useAIModelStore();
  return {
    selectedModel: store.selectedModel,
    selectedProvider: store.selectedProvider,
    setSelectedModel: store.setSelectedModel,
    setSelectedProvider: store.setSelectedProvider,
    getModelsByProvider: store.getModelsByProvider,
    getAllModels: store.getAllModels
  };
};
